import { Link } from 'react-router-dom'
import { formatToBRL } from '@cannect/utils/formatters'
import { cycleStatusLabel } from '@cannect/services/resources/recurrency/constants'
import { formatApiDate } from '@cannect/services/resources/recurrency/helpers'
import type { CycleView, SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { cn } from '@cannect/lib/utils'
import { subscriptionsCopy } from '../copy'

const copy = subscriptionsCopy.cycles

const isCurrent = (cycle: CycleView, detail: SubscriptionDetail) =>
  Boolean(detail.currentCycle) &&
  (detail.currentCycle?.id && cycle.id
    ? cycle.id === detail.currentCycle.id
    : cycle.number === detail.currentCycle?.number)

export const CyclesTimeline = ({ detail }: { detail: SubscriptionDetail }) => {
  const cycles = [...(detail.cycles || [])].sort((a, b) => b.number - a.number)

  return (
    <section
      aria-labelledby="sub-cycles"
      className="flex flex-col gap-4 rounded-xl border border-solid border-neutral-100 bg-white p-4 md:p-6">
      <h2 id="sub-cycles" className="text-lg font-semibold text-neutral-800">
        {subscriptionsCopy.sections.cycles}
      </h2>
      {!cycles.length && <p className="text-sm text-neutral-600">{copy.empty}</p>}
      <ol className="flex list-none flex-col gap-3 p-0">
        {cycles.map((cycle) => {
          const current = isCurrent(cycle, detail)
          const amount = cycle.chargedAmount ?? cycle.notifiedAmount
          return (
            <li
              key={cycle.id ?? cycle.number}
              aria-current={current ? 'step' : undefined}
              className={cn(
                'flex flex-col gap-1 rounded-lg border border-solid p-3 text-sm',
                current ? 'border-primary-700 bg-primary-50' : 'border-neutral-100'
              )}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-neutral-800">
                  {copy.cycle(cycle.number)}
                  {current && <span className="ml-2 text-xs font-normal text-primary-700">{copy.current}</span>}
                </span>
                <span className="text-neutral-700">{cycleStatusLabel(cycle.status)}</span>
              </div>
              <span className="text-neutral-600">
                {copy.scheduled}: {formatApiDate(cycle.scheduledAt) || '—'}
              </span>
              {amount !== null && amount !== undefined && (
                <span className="text-neutral-600">
                  {cycle.chargedAmount !== null && cycle.chargedAmount !== undefined ? copy.charged : copy.notified}:{' '}
                  {formatToBRL(amount)}
                </span>
              )}
              {cycle.paymentAttempts > 1 && (
                <span className="text-neutral-600">{copy.attempts(cycle.paymentAttempts)}</span>
              )}
              {cycle.orderId && (
                <Link
                  to={`/detalhes-do-pedido/${cycle.orderId}`}
                  className="self-start font-semibold text-primary-700 underline">
                  {subscriptionsCopy.buttons.viewOrder}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </section>
  )
}
